import React, { useState, useEffect } from "react";
import IngredientAPI from "../../api/ingredient";
import TradenameCard from "./TradenameCard";
import Carousel from "../Carousel";

/**
 * Shows other trade names that share the same active ingredients.
 * Fetches related products from the first ingredient's tradenames endpoint,
 * then filters out the current product.
 */
export default function RelatedTradenames({ product }) {
  const [relatedItems, setRelatedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ingredientName, setIngredientName] = useState("");

  useEffect(() => {
    if (!product?.ingredients?.length) {
      setIsLoading(false);
      return;
    }

    const fetchRelated = async () => {
      try {
        setIsLoading(true);

        // Use the first ingredient to find related trade names
        const primaryIngredient = product.ingredients[0];
        const data = await IngredientAPI.getTradeNamesForIngredient(
          primaryIngredient.uri
        );

        setIngredientName(data.ingredient?.name || primaryIngredient.name);

        // Filter out the current product and limit to 12 items
        const filtered = (data.tradenames || [])
          .filter((item) => item.uri !== product.uri)
          .slice(0, 12);

        setRelatedItems(filtered);
      } catch (err) {
        console.error("Error fetching related tradenames:", err);
        setRelatedItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelated();
  }, [product?.uri, product?.ingredients]);

  // Don't render anything if loading finishes with no results
  if (!isLoading && relatedItems.length === 0) return null;

  // Don't render while loading if there are no ingredients
  if (!product?.ingredients?.length) return null;

  const heading =
    product.ingredients.length === 1
      ? `Other medicines with ${ingredientName}`
      : "Other medicines with similar ingredients";

  return (
    <section className="mt-8" aria-label={heading}>
      <h2 className="text-18 font-semibold text-secondary mb-4">{heading}</h2>

      {isLoading ? (
        <RelatedSkeleton />
      ) : relatedItems.length <= 3 ? (
        // Few items: simple row without carousel
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {relatedItems.map((item) => (
            <TradenameCard key={item.uri} item={item} />
          ))}
        </div>
      ) : (
        // Many items: carousel
        <Carousel
          rows={1}
          slidesToShow={3}
          slidesToScroll={3}
          isInfinite={relatedItems.length > 3}
        >
          {relatedItems.map((item) => (
            <div key={item.uri} className="pb-4 px-1.5 h-full">
              <TradenameCard item={item} />
            </div>
          ))}
        </Carousel>
      )}
    </section>
  );
}

function RelatedSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex p-3 h-[120px] border border-white/60 rounded-xl bg-white/40 animate-pulse"
        >
          <div className="flex-shrink-0 mr-3">
            <div className="w-9 h-9 rounded-full bg-secondary/8" />
          </div>
          <div className="flex-1">
            <div className="h-4 w-32 rounded bg-secondary/8 mb-2" />
            <div className="h-3 w-24 rounded bg-secondary/6 mb-1.5" />
            <div className="h-3 w-20 rounded bg-secondary/5" />
          </div>
        </div>
      ))}
    </div>
  );
}
